import { Link } from "@tanstack/react-router";
import { usePersona } from "@/lib/persona";
import { cn } from "@/lib/utils";
import type { ReactNode } from "react";

/**
 * "Eat With Me presents Taste of Tulum by Art With Me" — the shared brand
 * lockup used by every persona shell's header. `compact` pins the small
 * (mobile-tier) sizing regardless of viewport, for use inside narrow
 * sidebars where Tailwind's `sm:` breakpoint (which tracks viewport width,
 * not container width) would otherwise still trigger the larger size.
 */
export function BrandLockup({ compact = false }: { compact?: boolean }) {
  const persona = usePersona();
  const sideWidth = compact ? "w-10" : "w-10 sm:w-14";
  const markHeight = compact ? "h-[26px]" : "h-[26px] sm:h-[33px]";
  const dividerHeight = compact ? "h-6" : "h-6 sm:h-8";

  return (
    <Link
      to="/"
      aria-label={`Eat With Me presents ${persona.brand.name} by Art With Me`}
      className={cn("flex shrink-0 items-center gap-1", !compact && "sm:gap-1.5")}
    >
      <span className="relative shrink-0">
        <img src={persona.brand.logoSrc.presents} alt="" className={cn("h-auto", sideWidth)} />
        <span
          className={cn(
            "absolute left-0 top-full mt-0.5 flex justify-between font-sans text-[8px] font-extrabold uppercase text-[#e3c071]",
            sideWidth
          )}
        >
          {"PRESENTS".split("").map((letter, i) => (
            <i key={i} className="not-italic">
              {letter}
            </i>
          ))}
        </span>
      </span>
      <span className={cn("w-px shrink-0 bg-[#e3c071]/30", dividerHeight)} />
      <img src={persona.brand.logoSrc.mark} alt="" className={cn("w-auto", markHeight)} />
      <span className={cn("w-px shrink-0 bg-[#e3c071]/30", dividerHeight)} />
      <span className="relative shrink-0">
        <span className="absolute bottom-full left-0 mb-0.5 whitespace-nowrap font-sans text-[8px] font-extrabold uppercase tracking-[0.24em] text-[#e3c071]">
          By
        </span>
        <img src={persona.brand.logoSrc.by} alt="" className={cn("h-auto", sideWidth)} />
      </span>
    </Link>
  );
}

/**
 * Dark-green sticky brand header — `BrandLockup` on the left, persona-specific
 * chrome (nav, role badge, sign out, ...) passed in as `children` on the right.
 *
 * `stacked` renders `children` centered below the lockup instead of beside
 * it — for narrow sidebar contexts where there isn't enough horizontal room
 * next to the lockup for even a short label (e.g. the Organizer sidebar).
 */
export function BrandHeader({
  children,
  className,
  compact = false,
  stacked = false,
}: {
  children?: ReactNode;
  className?: string;
  compact?: boolean;
  stacked?: boolean;
}) {
  if (stacked) {
    return (
      <header className={cn("sticky top-0 z-30 border-b border-[#e3c071]/20 bg-[#12291d]", className)}>
        <div className="container flex flex-col items-center gap-3 py-3">
          <BrandLockup compact={compact} />
          {children}
        </div>
      </header>
    );
  }

  return (
    <header className={cn("sticky top-0 z-30 border-b border-[#e3c071]/20 bg-[#12291d]", className)}>
      <div className="container flex h-14 items-center justify-between gap-4">
        <BrandLockup compact={compact} />
        {children}
      </div>
    </header>
  );
}
